import React from 'react';
import {
  Stack, Text, UnorderedList, withTranslation, StyledContainer, Trans,
} from '@piwikpro/ui-components';
import { TFunction } from 'react-i18next';

export type SubscriptionUpdateAction = 'upgrade' | 'downgrade' | 'upgrade-billing-cycle-only' | 'downgrade-billing-cycle-only'

interface ConfirmationModalBodyProps {
  action: SubscriptionUpdateAction
  immediateTransactionPrice: string
  recurringTransactionPrice: string
  nextBillingPeriod: string
  billingCycle: string
  creditsIssued: boolean
  t: TFunction
}

const ConfirmationModalBody: React.FunctionComponent<ConfirmationModalBodyProps> = (
  {
    t,
    action,
    immediateTransactionPrice,
    recurringTransactionPrice,
    nextBillingPeriod,
    billingCycle,
    creditsIssued,
  },
) => {
  let body;

  switch (action) {
    case 'upgrade':
      body = (
        <Stack vertical>
          <Stack.Item>
            <Text>{t('marketing.change-plan-overlay.upgrade-confirmation.description-0')}</Text>
          </Stack.Item>
          <Stack.Item previousSpacing="none">
            <UnorderedList>
              <UnorderedList.Item>
                <Text>{t('marketing.change-plan-overlay.upgrade-confirmation.description-1')}</Text>
              </UnorderedList.Item>
              <UnorderedList.Item>
                {!creditsIssued ? (
                  <Trans
                    i18nKey="marketing.change-plan-overlay.upgrade-confirmation.description-2"
                    values={{ value: immediateTransactionPrice }}
                    components={[<b>text</b>]}
                  />
                ) : (
                  <Text>
                    {t('marketing.change-plan-overlay.upgrade-confirmation.description-2-with-credits', {
                      value: immediateTransactionPrice,
                      date: nextBillingPeriod,
                    })}
                  </Text>
                )}
              </UnorderedList.Item>
              <UnorderedList.Item>
                {!creditsIssued ? (
                  <Trans
                    i18nKey="marketing.change-plan-overlay.upgrade-confirmation.description-3"
                    values={{
                      date: nextBillingPeriod,
                      value: recurringTransactionPrice,
                      billingCycle,
                    }}
                    components={[<b>text</b>]}
                  />
                ) : (
                  <Trans
                    i18nKey="marketing.change-plan-overlay.upgrade-confirmation.description-3-with-credits"
                    values={{
                      value: recurringTransactionPrice,
                      billingCycle,
                    }}
                    components={[<b>text</b>]}
                  />
                )}
              </UnorderedList.Item>
            </UnorderedList>
          </Stack.Item>
          <Stack.Item previousSpacing="narrow">
            <Text>{t('marketing.change-plan-overlay.upgrade-confirmation.description-4')}</Text>
          </Stack.Item>
        </Stack>
      );
      break;
    case 'upgrade-billing-cycle-only':
      body = (
        <Stack vertical>
          <Stack.Item>
            <Text>{t('marketing.change-plan-overlay.upgrade-billing-change-only.description-0')}</Text>
          </Stack.Item>
          <Stack.Item previousSpacing="none">
            <UnorderedList>
              <UnorderedList.Item>
                <Text>{t('marketing.change-plan-overlay.upgrade-billing-change-only.description-1')}</Text>
              </UnorderedList.Item>
              <UnorderedList.Item>
                <Trans
                  i18nKey="marketing.change-plan-overlay.upgrade-billing-change-only.description-2"
                  values={{
                    value: immediateTransactionPrice,
                  }}
                  components={[<b>text</b>]}
                />
              </UnorderedList.Item>
              <UnorderedList.Item>
                <Trans
                  i18nKey="marketing.change-plan-overlay.upgrade-billing-change-only.description-3"
                  values={{
                    date: nextBillingPeriod,
                    value: recurringTransactionPrice,
                    billingCycle,
                  }}
                  components={[<b>text</b>]}
                />
              </UnorderedList.Item>
            </UnorderedList>
          </Stack.Item>
          <Stack.Item previousSpacing="narrow">
            <Text>{t('marketing.change-plan-overlay.upgrade-billing-change-only.description-4')}</Text>
          </Stack.Item>
        </Stack>
      );
      break;

    case 'downgrade-billing-cycle-only':
      body = (
        <Stack vertical>
          <Stack.Item>
            <Text>{t('marketing.change-plan-overlay.downgrade-billing-change-only.description-0')}</Text>
          </Stack.Item>
          <Stack.Item previousSpacing="none">
            <UnorderedList>
              <UnorderedList.Item>
                <Text>{t('marketing.change-plan-overlay.downgrade-billing-change-only.description-1')}</Text>
              </UnorderedList.Item>
              <UnorderedList.Item>
                <Trans
                  i18nKey="marketing.change-plan-overlay.downgrade-billing-change-only.description-2"
                  values={{
                    date: nextBillingPeriod,
                    value: recurringTransactionPrice,
                    billingCycle,
                  }}
                  components={[<b>text</b>]}
                />
              </UnorderedList.Item>
            </UnorderedList>
          </Stack.Item>
          <Stack.Item previousSpacing="narrow">
            <Text>{t('marketing.change-plan-overlay.downgrade-billing-change-only.description-3')}</Text>
          </Stack.Item>
        </Stack>
      );
      break;
    case 'downgrade':
    default:
      body = (
        <Stack vertical>
          <Stack.Item>
            <Text>
              <Trans
                i18nKey="marketing.change-plan-overlay.downgrade-confirmation.description-0"
                values={{
                  date: nextBillingPeriod,
                  value: recurringTransactionPrice,
                  billingCycle,
                }}
                components={[<b>text</b>]}
              />
            </Text>
          </Stack.Item>
          <Stack.Item previousSpacing="narrow">
            <Text>{t('marketing.change-plan-overlay.downgrade-confirmation.description-1')}</Text>
          </Stack.Item>
        </Stack>
      );
      break;
  }

  return (
    <StyledContainer padding="extraNarrow">
      <Stack vertical spacing="narrow">
        {body}
      </Stack>
    </StyledContainer>
  );
};

export default withTranslation(['administration'])(ConfirmationModalBody);
